import { AxiosInstance } from 'axios'
import {
  FavoriteTrip,
  GetTripsParams,
  TripDetailed,
  TripShort,
  UpdateTripPurposePayload,
  UpdateTripsPayload
} from 'types/trips'
import { cleanObject, formatDateParams } from 'utils/Functions'
import { TRIPS_ENDPOINTS } from 'api/ApiRoutes'
import { Group } from 'types/groups'
import { FormValues as CreateTripPayload } from 'core/widgets/trip-detail/TripDetail'
import { ID } from 'types/utility'
import qs from 'qs'
import { DateRangeFilterInAPI } from 'types/reports'

const mapBoxBaseURL = 'https://api.mapbox.com/geocoding/v5'
const mapBoxKey = process.env.REACT_APP_MAPBOX_KEY

class TripsApi {
  constructor(private axiosInstance: AxiosInstance) {}

  getTrips = async (params?: Partial<GetTripsParams>) => {
    const { date_range, purpose, ...rest } = params ?? {}
    const apiParams = cleanObject({
      ...rest,
      purpose: purpose !== 'Unclassified' ? purpose : undefined,
      uncategorized: purpose === 'Unclassified',
      ...formatDateParams(date_range ?? [])
    })
    const { data } = await this.axiosInstance.get<TripDetailed[]>(
      TRIPS_ENDPOINTS.BASE,
      {
        params: apiParams
      }
    )
    return data
  }

  getTrip = async (tripId: TripShort['token_id']) => {
    const { data } = await this.axiosInstance.get<TripShort>(
      TRIPS_ENDPOINTS.TRIP(tripId)
    )
    return data
  }

  getReportTrip = async (
    tripId: TripShort['token_id'],
    teamId: Group['token_id']
  ) => {
    const { data } = await this.axiosInstance.get<TripShort>(
      TRIPS_ENDPOINTS.REPORT_TRIP(teamId, tripId)
    )
    return data
  }

  createTrip = async (data: CreateTripPayload) => {
    if (!data.miles && !data.kms)
      throw Error('no distance', data as ErrorOptions)
    const form_data = new FormData()

    if (data.photo) {
      form_data.append('trip[photo_attributes][image]', data.photo as File)
    }

    if (data.locations) {
      data.locations.forEach((val) => {
        form_data.append(`trip[locations][]`, String(val))
      })
    }

    if (data.tags) {
      data.tags?.forEach((val) => {
        form_data.append(`trip[tags][]`, String(val))
      })
    }

    for (const key in data) {
      const item = data[key as keyof CreateTripPayload]
      if (
        key === 'photo' ||
        key === 'locations' ||
        key === 'tags' ||
        item === undefined
      )
        continue
      form_data.append('trip[' + key + ']', item as Blob)
    }

    const { data: response } = await this.axiosInstance.post(
      TRIPS_ENDPOINTS.BASE,
      form_data,
      {
        headers: { 'Content-Type': 'multipart/form-data' }
      }
    )
    return response
  }

  updateTrip = async (
    data: CreateTripPayload & { token_id?: TripShort['token_id'] }
  ) => {
    if (!data.token_id) throw Error('no token_id', data as ErrorOptions)
    if (!data.miles && !data.kms)
      throw Error('no distance', data as ErrorOptions)
    const form_data = new FormData()

    if (data.photo) {
      form_data.append('trip[photo_attributes][image]', data.photo as File)
    }

    if (!data.photo) {
      form_data.append(`trip[photo_attributes][_destroy]`, 'true')
    }

    if (data.locations) {
      data.locations.forEach((val) => {
        form_data.append(`trip[locations][]`, String(val))
      })
    }

    if (data.tags?.length) {
      data.tags?.forEach((val) => {
        form_data.append(`trip[tags][]`, String(val))
      })
    }
    if (!data.tags?.length) {
      form_data.append(`trip[tags][]`, '')
    }

    for (const key in data) {
      const item = data[key as keyof CreateTripPayload]
      if (
        key === 'photo' ||
        key === 'locations' ||
        key === 'tags' ||
        item === undefined
      )
        continue
      form_data.append('trip[' + key + ']', item as Blob)
    }
    const { data: response } = await this.axiosInstance.put(
      TRIPS_ENDPOINTS.TRIP(data.token_id),
      form_data,
      {
        headers: { 'Content-Type': 'multipart/form-data' }
      }
    )
    return response
  }

  deleteTrip = async (tripId: TripShort['token_id']) => {
    const { data } = await this.axiosInstance.delete(
      TRIPS_ENDPOINTS.TRIP(tripId)
    )
    return data
  }

  updateTripPurpose = async (
    tripId: TripShort['token_id'],
    trip: UpdateTripPurposePayload
  ) => {
    const { data } = await this.axiosInstance.put<TripShort>(
      TRIPS_ENDPOINTS.TRIP(tripId),
      { trip }
    )
    return data
  }

  updateTripNotes = async ({
    tripId,
    notes
  }: {
    tripId: TripShort['token_id']
    notes: string
  }) => {
    const { data } = await this.axiosInstance.put<TripShort>(
      TRIPS_ENDPOINTS.TRIP(tripId),
      { trip: { notes } }
    )
    return data
  }

  mergeTrips = async (tripIds: ID[]) => {
    const { data } = await this.axiosInstance.post(
      TRIPS_ENDPOINTS.MERGE_TRIPS,
      {
        merge_trips_token_ids: tripIds
      }
    )
    return data
  }

  undoMergeTrips = async (tripId: ID) => {
    const { data } = await this.axiosInstance.delete(
      TRIPS_ENDPOINTS.MERGE_TRIP_BY_ID(tripId)
    )
    return data
  }

  batchDeleteTrips = async (payload: UpdateTripsPayload['filter_params']) => {
    const { date_range, ...rest } = payload ?? {}
    const apiPayload = cleanObject({
      ...rest,
      ...formatDateParams(date_range !== null ? date_range : []),
      limit: null
    })

    const query = qs.stringify(
      { filter_params: apiPayload },
      { arrayFormat: 'brackets' }
    )
    const { data } = await this.axiosInstance.delete(
      `${TRIPS_ENDPOINTS.BATCH_DELETE_TRIPS}?${query}`
    )
    return data
  }

  batchUpdateTrips = ({ filter_params, update_params }: UpdateTripsPayload) => {
    const { date_range, ...rest } = filter_params ?? {}
    if (rest.purpose === 'uncategorized' || rest.purpose === 'Unclassified') {
      rest.purpose = undefined
      rest.uncategorized = true
    }
    const modified_filter_params = cleanObject({
      ...rest,
      ...formatDateParams<DateRangeFilterInAPI>(date_range),
      limit: null
    })
    const payload = {
      filter_params: modified_filter_params,
      update_params
    }
    return this.axiosInstance.put(TRIPS_ENDPOINTS.BATCH_UPDATE_TRIPS, payload)
  }

  getMapBoxPlaces = async ({
    search,
    proximity = 'ip'
  }: {
    search: string
    proximity?: string | 'ip'
  }) => {
    // proximity can be a string of lat,lng or 'ip'
    const d = await this.axiosInstance.get(
      `${mapBoxBaseURL}/mapbox.places/${search}.json?proximity=${proximity}&access_token=${mapBoxKey}`
    )
    const result: { id: string; place_name: string }[] = d.data?.features
    return result
  }

  getFavoriteTrips = async () => {
    const { data } = await this.axiosInstance.get<FavoriteTrip[]>(
      TRIPS_ENDPOINTS.FAVORITE_TRIPS
    )
    return data
  }
  getFavoriteTrip = async (token_id: ID) => {
    const { data } = await this.axiosInstance.get<FavoriteTrip>(
      TRIPS_ENDPOINTS.FAVORITE_TRIP(token_id)
    )
    return data
  }
  createFavoriteTrip = async (params: Partial<FavoriteTrip>) => {
    const { data } = await this.axiosInstance.post<FavoriteTrip>(
      TRIPS_ENDPOINTS.FAVORITE_TRIPS,
      {
        ...params
      }
    )
    return data
  }
  updateFavoriteTrip = async ({
    token_id,
    ...params
  }: Partial<FavoriteTrip>) => {
    const { data } = await this.axiosInstance.put<FavoriteTrip>(
      TRIPS_ENDPOINTS.FAVORITE_TRIP(token_id as ID),
      {
        ...params
      }
    )
    return data
  }
  deleteFavoriteTrip = async (token_id: ID) => {
    const { data } = await this.axiosInstance.delete<{ result: string }>(
      TRIPS_ENDPOINTS.FAVORITE_TRIP(token_id)
    )
    return data
  }
}

export default TripsApi
